import { useEffect, useMemo, useState } from "react";
import { Inbox, Search } from "lucide-react";
import { api, apiErrorDetail } from "@/lib/api";
import { romeToday } from "@/lib/site";
import ApptRow, { STATUS_FILTERS } from "./ApptRow";

/* Tutte le richieste di appuntamenti in un'unica lista, qualunque sia la data. */
export default function RequestsTab({ flash, contact }) {
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("tutti");
  const [servicesList, setServicesList] = useState([]);

  const load = () => {
    api
      .get("/admin/appointments")
      .then(({ data }) => setItems(data.appointments || []))
      .catch((e) => flash(apiErrorDetail(e), false));
    api
      .get("/site")
      .then(({ data }) => setServicesList((data.services || []).filter((s) => s.active !== false)))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (a, status) => {
    try {
      await api.put(`/admin/appointments/${a.mid}`, {
        service_id: a.service_id, date: a.date, start: a.start, name: a.name, phone: a.phone || "", email: a.email || "", status,
      });
      flash(
        status === "confirmed" ? "Appuntamento confermato"
          : status === "rejected" ? "Richiesta rifiutata: orario liberato"
            : status === "cancelled" ? "Appuntamento annullato: orario liberato"
              : "Stato aggiornato",
      );
      load();
    } catch (e) {
      flash(apiErrorDetail(e), false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Eliminare definitivamente l'appuntamento di ${a.name}?`)) return;
    try {
      await api.delete(`/admin/appointments/${a.mid}`);
      flash("Appuntamento eliminato");
      load();
    } catch (e) {
      flash(apiErrorDetail(e), false);
    }
  };

  const modeOf = (a) => servicesList.find((s) => s.id === a.service_id)?.mode || "studio";

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = search.trim().toLowerCase();
    return items
      .filter((a) => (statusFilter === "tutti" ? true : a.status === statusFilter))
      .filter((a) => !q || [a.name, a.phone, a.email, a.service_name, a.date].some((v) => (v || "").toLowerCase().includes(q)));
  }, [items, search, statusFilter]);

  const today = romeToday();
  const upcoming = filtered.filter((a) => a.date >= today);
  const past = filtered.filter((a) => a.date < today).sort((a, b) => (b.date + b.start).localeCompare(a.date + a.start));

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display text-2xl lg:text-3xl flex items-center gap-3">
          <Inbox size={24} className="text-deeptaupe" /> Richieste di appuntamenti
        </h2>
        <p className="text-sm text-charcoal/60 mt-2 max-w-2xl">
          Tutte le richieste arrivate dal sito e dagli appuntamenti manuali, di qualunque data: in arrivo prima, poi quelle passate.
          Per spostare o modificare un orario usa la sezione Appuntamenti.
        </p>
      </div>

      <label className="block relative mb-4">
        <Search size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-charcoal/40" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per nome, telefono, email, servizio o data…"
          data-testid="requests-search"
          className="w-full rounded-full border border-taupe/40 bg-white px-12 py-3 text-sm focus:border-deeptaupe focus:outline-none"
        />
      </label>

      <nav className="flex gap-2 overflow-x-auto pb-1 mb-6" data-testid="requests-status-filters">
        {STATUS_FILTERS.map((f) => {
          const count = (items || []).filter((a) => (f.key === "tutti" ? true : a.status === f.key)).length;
          return (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              data-testid={`requests-filter-${f.key}`}
              className={`shrink-0 rounded-full px-3.5 py-2 text-[11px] uppercase tracking-widest2 font-medium transition-colors duration-300 ${statusFilter === f.key ? "bg-charcoal text-ivory" : "bg-white border border-taupe/30 text-charcoal/60 hover:bg-peach"}`}
            >
              {f.label} ({count})
            </button>
          );
        })}
      </nav>

      {items === null ? (
        <p className="py-16 text-center text-charcoal/50 text-sm" data-testid="requests-loading">Caricamento delle richieste…</p>
      ) : filtered.length === 0 ? (
        <p className="py-16 text-center text-charcoal/50 text-sm bg-white rounded-3xl" data-testid="requests-empty">
          {items.length === 0 ? "Non ci sono ancora richieste di appuntamenti." : "Nessuna richiesta trovata con questi filtri."}
        </p>
      ) : (
        <div data-testid="requests-list">
          {upcoming.length > 0 && (
            <div className="space-y-2">
              {past.length > 0 && (
                <p className="text-[11px] uppercase tracking-widest2 text-charcoal/50 font-medium">In arrivo</p>
              )}
              {upcoming.map((a) => (
                <ApptRow key={a.mid} a={a} onConfirm={setStatus} onCancel={setStatus} onDelete={remove} mode={modeOf(a)} contact={contact} />
              ))}
            </div>
          )}
          {past.length > 0 && (
            <div className="space-y-2 mt-6">
              {upcoming.length > 0 && (
                <p className="text-[11px] uppercase tracking-widest2 text-charcoal/50 font-medium">Passati</p>
              )}
              {past.map((a) => (
                <ApptRow key={a.mid} a={a} onConfirm={setStatus} onCancel={setStatus} onDelete={remove} mode={modeOf(a)} contact={contact} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
